import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Mail, Search, Loader2, CheckCircle2, Unlink, RefreshCw,
  Paperclip, Trash2, Play, Star, Inbox, Eye,
  Calendar, User, FileText, X, Clock, ArrowUpRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─ Helpers ─
function parseEmailDate(dateStr) {
  if (!dateStr) return null;

  try { return new Date(dateStr); } catch { return null; }
}

// No-op headers helper (auth removed — Gmail OAuth handled server-side)
const getAuthHeaders = () => ({});
function dateGroup(dateStr) {
  const d = parseEmailDate(dateStr);
  if (!d) return 'Older';
  const now = new Date();
  const today     = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const weekAgo   = new Date(today); weekAgo.setDate(today.getDate() - 7);
  const monthAgo  = new Date(today); monthAgo.setDate(today.getDate() - 30);
  const emailDay  = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (emailDay >= today)     return 'Today';
  if (emailDay >= yesterday) return 'Yesterday';
  if (emailDay >= weekAgo)   return 'This Week';
  if (emailDay >= monthAgo)  return 'This Month';
  return 'Older';
}
function groupEmails(emails) {
  const ORDER = ['Today', 'Yesterday', 'This Week', 'This Month', 'Older'];
  const map = {};
  ORDER.forEach(k => { map[k] = []; });
  emails.forEach(e => { const g = dateGroup(e.date); if (map[g]) map[g].push(e); });
  return ORDER.map(k => ({ label: k, items: map[k] })).filter(g => g.items.length > 0);
}
function formatDateShort(dateStr) {
  const d = parseEmailDate(dateStr);
  if (!d) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}
function formatDateFull(dateStr) {
  const d = parseEmailDate(dateStr);
  if (!d) return dateStr || '';
  return d.toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ─ Gmail icon ─
function GmailIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="3" fill="white" stroke="#e0e0e0" strokeWidth="0.5"/>
      <path d="M2 6l10 7L22 6" stroke="#EA4335" strokeWidth="2" fill="none"/>
      <path d="M2 6v12h20V6" fill="none" stroke="#DADCE0" strokeWidth="1"/>
    </svg>
  );
}

// ─ Attachment chip ─
function AttChip({ filename, size }) {
  const ext = filename.split('.').pop().toLowerCase();
  const cls = ext === 'pdf'  ? 'text-red-600 bg-red-50 border-red-100'
            : ext === 'csv'  ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
            : 'text-blue-700 bg-blue-50 border-blue-100';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono border ${cls}`}>
      <Paperclip className="w-2.5 h-2.5" />
      <span className="max-w-[130px] truncate">{filename}</span>
      {size > 0 && <span className="opacity-50">{Math.round(size / 1024)}kb</span>}
    </span>
  );
}

// ──────────────────────────────────────────
// EMAIL VIEW MODAL
// ──────────────────────────────────────────
function EmailViewModal({ email, onClose, onAudit, onSave, saving }) {
  const [body, setBody]         = useState(email.body || '');
  const [loading, setLoading]   = useState(!email.body);
  const isSaved   = !!email._id;
  const isAudited = !!email.is_audited;

  // Fetch full body if not already stored.
  // Saved email → /api/gmail/saved/:id/content
  // Unsaved Gmail message (from Inbox/Search) → /api/gmail/message/:gmail_message_id
  useEffect(() => {
    if (email.body) { setBody(email.body); return; }
    setLoading(true);

    let url = null;
    if (email._id) {
      url = `${BACKEND}/api/gmail/saved/${email._id}/content`;
    } else if (email.gmail_message_id) {
      url = `${BACKEND}/api/gmail/message/${email.gmail_message_id}`;
    }

    if (!url) { setLoading(false); return; }

    fetch(url)
      .then(r => r.json())
      .then(d => setBody(d.body || ''))
      .catch(() => setBody('(Could not load email body)'))
      .finally(() => setLoading(false));
  }, [email._id, email.gmail_message_id, email.body]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // ── Smart body renderer ───────────────────────────────────────────
  function renderBody(text) {
    if (!text) return <p className="text-muted-foreground text-sm italic px-6 py-8">No email body available.</p>;

    const lines = text.split(/\r?\n/);
    const elements = [];
    let i = 0;
    let tableHeader = null;
    let tableRows = [];
    let weekTableHeader = null;
    let weekTableRows = [];

    const flushProductTable = (key) => {
      if (!tableRows.length) return;

      // Collect all content: header + rows
      const allLines = tableHeader ? [tableHeader, ...tableRows] : tableRows;

      elements.push(
        <div key={`pt-${key}`}
          className="my-3 rounded-xl overflow-hidden border border-slate-700"
          style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}
        >
          {/* Title bar */}
          <div style={{ background: '#0f172a', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#94a3b8', opacity: 0.4, display: 'inline-block' }} />
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#94a3b8', opacity: 0.2, display: 'inline-block' }} />
            <span style={{ fontSize: 10, fontFamily: 'Outfit, sans-serif', fontWeight: 600, color: '#64748b', marginLeft: 6 }}>
              Product Inventory Table
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 9, color: '#475569', fontFamily: 'IBM Plex Mono, monospace' }}>
              {tableRows.length} SKUs
            </span>
          </div>

          {/* Scrollable data view */}
          <div style={{ overflowX: 'auto', overflowY: 'visible', background: '#0f172a', WebkitOverflowScrolling: 'touch' }}>
            {allLines.map((line, li) => {
              const t = line.trim();
              const isHeader = li === 0 && tableHeader;
              const isSamsung = !isHeader && /^SAMSUNG|^APPLE|^NOKIA|^HUAWEI/i.test(t);

              // Detect high-WOS lines (numbers like 24.6, 36.0, etc.)
              const wosMatch = !isHeader && !isSamsung && t.match(/\b(\d{2,3}\.\d)\b/);
              const highWOS = wosMatch && parseFloat(wosMatch[1]) > 8;
              const medWOS  = wosMatch && parseFloat(wosMatch[1]) >= 4 && parseFloat(wosMatch[1]) <= 8;

              // Colour scheme
              let bg, color, fw;
              if (isHeader) {
                bg = '#1e3a5f'; color = '#93c5fd'; fw = 700;
              } else if (isSamsung) {
                bg = 'rgba(59,130,246,0.12)'; color = '#bfdbfe'; fw = 600;
              } else if (highWOS) {
                bg = 'rgba(239,68,68,0.08)'; color = '#fca5a5'; fw = 400;
              } else if (medWOS) {
                bg = 'rgba(251,191,36,0.06)'; color = '#fde68a'; fw = 400;
              } else {
                bg = li % 2 === 0 ? '#111827' : '#0f172a'; color = '#6b7280'; fw = 400;
              }

              return (
                <div key={li} style={{
                  padding: isSamsung ? '3px 14px' : '1.5px 14px',
                  fontSize: 10,
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontWeight: fw,
                  whiteSpace: 'pre',
                  color,
                  background: bg,
                  borderBottom: isSamsung ? '1px solid rgba(59,130,246,0.15)' : 'none',
                  lineHeight: 1.55,
                  letterSpacing: isHeader ? '0.03em' : 0,
                }}>
                  {line}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{ background: '#1e293b', borderTop: '1px solid #334155', padding: '5px 14px', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {[
              { dot: '#93c5fd', label: 'Product row' },
              { dot: '#fca5a5', label: 'WOS > 8w (Flag)' },
              { dot: '#fde68a', label: 'WOS 4–8w (Check)' },
            ].map(({ dot, label }) => (
              <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 9, color: '#475569' }}>
                <span style={{ width: 7, height: 7, borderRadius: 2, background: dot, display: 'inline-block' }} />
                {label}
              </span>
            ))}
          </div>
        </div>
      );
      tableHeader = null;
      tableRows = [];
    };
    const flushWeekTable = (key) => {
      if (!weekTableRows.length) return;
      elements.push(
        <div key={`wt-${key}`} className="rounded-lg border border-blue-100 bg-blue-50/40 overflow-hidden mb-3">
          {weekTableHeader && (
            <div className="px-3 py-1.5 bg-blue-100/60 border-b border-blue-100">
              <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600">{weekTableHeader}</p>
            </div>
          )}
          <div className="divide-y divide-blue-100/60">
            {weekTableRows.map((row, ri) => (
              <div key={ri} className="px-3 py-1.5 font-mono text-[11px] text-slate-600 flex gap-3 flex-wrap">
                {row.split(/\t|  +/).map(s => s.trim()).filter(Boolean).map((cell, ci) => (
                  <span key={ci} className={ci === 0 ? 'font-bold text-slate-700 w-10' : 'text-slate-500'}>{cell}</span>
                ))}
              </div>
            ))}
          </div>
        </div>
      );
      weekTableHeader = null;
      weekTableRows = [];
    };

    while (i < lines.length) {
      const line = lines[i];
      const t = line.trim();

      // Empty line → flush pending tables, add spacer
      if (!t) {
        flushProductTable(i);
        flushWeekTable(i);
        elements.push(<div key={`sp-${i}`} className="h-2" />);
        i++; continue;
      }

      // Forwarded / Original divider
      if (/^-{4,}\s*(Forwarded|Original)/i.test(t) || /^-{4,}$/.test(t)) {
        flushProductTable(i);
        flushWeekTable(i);
        elements.push(
          <div key={`fwd-${i}`} className="flex items-center gap-2 my-3">
            <div className="h-px flex-1 bg-blue-200" />
            <span className="px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest text-blue-500 bg-blue-50 rounded-full border border-blue-200">
              ✉ Forwarded Message
            </span>
            <div className="h-px flex-1 bg-blue-200" />
          </div>
        );
        i++; continue;
      }

      // Email meta headers (From: To: etc.)
      if (/^(From|To|Cc|Date|Subject):\s/i.test(t)) {
        const colon = t.indexOf(':');
        const label = t.slice(0, colon);
        const value = t.slice(colon + 1).trim();
        elements.push(
          <div key={`meta-${i}`} className="flex gap-2 text-[11px]">
            <span className="font-bold text-slate-500 w-14 flex-shrink-0">{label}</span>
            <span className="text-slate-700 break-all">{value}</span>
          </div>
        );
        i++; continue;
      }

      // MARKETING NAME table header → start product table
      if (/MARKETING NAME/i.test(t) && (/ITEM_CODE|WOS\b|RATE\b/i.test(t))) {
        flushWeekTable(i);
        tableHeader = t;
        tableRows = [];
        i++; continue;
      }

      // Product table data rows (Samsung or continuation)
      if (tableHeader !== null) {
        // End conditions
        const isEnd = /Kind Regards|Best Regards|^--$/i.test(t)
          || (/^[A-Z]/.test(t) && !/^SAMSUNG|^APPLE|^SM-|^S[A-Z]-/i.test(t) && t.length > 60 && !/\d{2,}/.test(t));
        if (isEnd) {
          flushProductTable(i);
        } else {
          tableRows.push(t);
          i++; continue;
        }
      }

      // WEEKS / Opening Stock table header
      if (/WEEKS\s+OPENING|OPENING STOCK VAL|SALE_VAL/i.test(t)) {
        weekTableHeader = t;
        weekTableRows = [];
        i++; continue;
      }

      // Week data rows (W10 W09 etc.)
      if (/^W\d{2}\s/.test(t) && weekTableHeader !== null) {
        weekTableRows.push(t);
        i++; continue;
      }
      if (weekTableRows.length > 0 && /^W\d{2}\s/.test(t)) {
        weekTableRows.push(t);
        i++; continue;
      }

      // INCREASING / REDUCING badges
      if (/^[\*\s]*(INCREASING|REDUCING)\s*[▲▼\*]*/i.test(t)) {
        const isInc = /INCREASING/i.test(t);
        elements.push(
          <div key={`dir-${i}`} className="my-1">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
              isInc ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                    : 'bg-red-100 text-red-600 border border-red-200'
            }`}>
              {isInc ? '▲' : '▼'} {isInc ? 'INCREASING' : 'REDUCING'}
            </span>
          </div>
        );
        i++; continue;
      }

      // WOS section headline (e.g. "20WEEKS MITSUMI GHANA PRODUCT WEEK OF STOCK")
      if (/\d+WEEKS.*MITSUMI|WEEK OF STOCK/i.test(t)) {
        elements.push(
          <p key={`wos-${i}`} className="text-[11px] font-bold text-slate-500 mt-2 uppercase tracking-wide">{t}</p>
        );
        i++; continue;
      }

      // TOTAL ABOVE … summary line
      if (/TOTAL ABOVE/i.test(t)) {
        elements.push(
          <div key={`tot-${i}`} className="px-3 py-1.5 rounded-lg bg-slate-100 border border-slate-200 text-[11px] font-mono text-slate-600 my-1">
            {t}
          </div>
        );
        i++; continue;
      }

      // WEEK nn data rows with $
      if (/^W\d{2}\s/.test(t)) {
        elements.push(
          <p key={`wk-${i}`} className="font-mono text-[11px] text-slate-500 leading-snug">{t}</p>
        );
        i++; continue;
      }

      // Intro / emphasis lines (bold markers *..*)
      if (/^\*[^*]+\*$/.test(t)) {
        elements.push(
          <p key={`em-${i}`} className="text-sm font-semibold text-foreground">{t.replace(/^\*|\*$/g, '')}</p>
        );
        i++; continue;
      }

      // Order amount line
      if (/Order amount/i.test(t)) {
        elements.push(
          <div key={`amt-${i}`} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/8 border border-primary/15 my-1">
            <span className="text-sm font-semibold text-primary">{t.replace(/\*/g, '')}</span>
          </div>
        );
        i++; continue;
      }

      // Greeting / intro
      if (/Good Morning|Good Afternoon|Dear|Hi |Hello /i.test(t)) {
        elements.push(<p key={`greet-${i}`} className="text-sm font-medium text-foreground">{t}</p>);
        i++; continue;
      }

      // Default text
      elements.push(
        <p key={`txt-${i}`} className="text-sm text-foreground/80 leading-relaxed">{t}</p>
      );
      i++;
    }

    // Flush any remaining
    flushProductTable('end');
    flushWeekTable('end');
    return elements;
  }

  const senderName  = email.from?.replace(/<[^>]+>/, '').trim();
  const senderEmail = email.from?.match(/<([^>]+)>/)?.[1] || email.from;
  const initials    = senderName?.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        style={{ background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(6px)' }}
        onClick={onClose}
      >
        {/* Modal — full flex col so body fills remaining height */}
        <motion.div
          initial={{ opacity: 0, y: 48, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 32, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          onClick={e => e.stopPropagation()}
          className="w-full sm:max-w-3xl rounded-t-3xl sm:rounded-2xl shadow-2xl flex flex-col bg-white"
          style={{ height: '92vh', maxHeight: '92vh' }}
        >
          {/* Header strip — flex-shrink-0 so it never compresses */}
          <div className="flex-shrink-0 rounded-t-3xl sm:rounded-t-2xl overflow-hidden"
            style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%)' }}>
            <div className="flex items-start justify-between px-5 pt-5 pb-3">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
                  style={{ background: 'rgba(59,130,246,0.35)', border: '1.5px solid rgba(96,165,250,0.4)' }}>
                  {initials}
                </div>
                <div className="min-w-0">
                  <h2 className="text-white font-semibold text-base leading-tight pr-6"
                    style={{ fontFamily: 'Space Grotesk' }}>
                    {email.subject || '(no subject)'}
                  </h2>
                  <p className="text-blue-100/80 text-xs mt-0.5 truncate">
                    {senderName && <span className="font-medium">{senderName}</span>}
                    {senderEmail && senderName !== senderEmail && (
                      <span className="ml-1 opacity-60">&lt;{senderEmail}&gt;</span>
                    )}
                  </p>
                  <p className="text-blue-300/60 text-[11px] mt-0.5">{formatDateFull(email.date)}</p>
                </div>
              </div>
              <button type="button" onClick={onClose}
                className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
                <X className="w-4 h-4 text-white/80" />
              </button>
            </div>

            {/* To / date meta */}
            {email.to && (
              <div className="px-5 pb-2 flex items-center gap-1.5 text-[11px] text-blue-200/60">
                <User className="w-3 h-3" />
                <span className="truncate">To: {email.to}</span>
              </div>
            )}

            {/* Attachments */}
            {email.attachments?.length > 0 && (
              <div className="px-5 pb-4 flex flex-wrap gap-2">
                {email.attachments.map((a, i) => (
                  <span key={i}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-mono"
                    style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(219,234,254,0.9)' }}>
                    <Paperclip className="w-3 h-3" />
                    {a.filename}
                    {a.size > 0 && <span style={{ opacity: 0.5 }}>{Math.round(a.size / 1024)}kb</span>}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Body — flex-1 + overflow-y-auto = scrollable */}
          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-5 space-y-1">
            {loading ? (
              <div className="space-y-2 pt-2">
                {[...Array(10)].map((_, idx) => (
                  <Skeleton key={idx} className={`h-4 ${idx % 3 === 2 ? 'w-2/3' : 'w-full'}`} />
                ))}
              </div>
            ) : (
              renderBody(body)
            )}
          </div>

          {/* Footer — flex-shrink-0 */}
          <div className="flex-shrink-0 px-5 py-3 border-t border-border bg-secondary/30 flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-muted-foreground">
              {email.attachments?.length > 0
                ? `${email.attachments.length} attachment${email.attachments.length > 1 ? 's' : ''} · `
                : ''}
              {isAudited ? 'Audited' : isSaved ? 'Saved for audit' : 'Not saved yet'}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onClose} className="h-8 text-xs">Close</Button>

              {/* Unsaved Gmail message → Save button */}
              {!isSaved && onSave && (
                <Button
                  size="sm"
                  onClick={() => onSave(email.gmail_message_id)}
                  disabled={saving}
                  className="h-8 text-xs gap-1.5"
                  data-testid="email-modal-save-button"
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
                  Save for Audit
                </Button>
              )}

              {/* Already audited → View Audit */}
              {isAudited && (
                <Button
                  size="sm"
                  onClick={() => { onAudit(email); onClose(); }}
                  className="h-8 text-xs gap-1.5 border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                  variant="outline"
                  data-testid="email-modal-view-audit-button"
                >
                  <Eye className="w-3 h-3" />View Audit
                </Button>
              )}

              {/* Saved & not audited → Run Audit */}
              {isSaved && !isAudited && (
                <Button
                  size="sm"
                  onClick={() => { onAudit(email); onClose(); }}
                  className="h-8 text-xs gap-1.5"
                  data-testid="email-modal-audit-button"
                >
                  <Play className="w-3 h-3" />Audit This Email
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─ Email row card ─
function EmailRow({ email, isSaved, onSave, onAudit, onDelete, onView, saving, auditing, mode = 'list' }) {
  const isAudited = !!email.is_audited;
  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className={`group rounded-xl border bg-white transition-all hover:shadow-sm ${
        isAudited ? 'border-blue-200 bg-blue-50/20'
        : isSaved ? 'border-emerald-200 bg-emerald-50/20'
        : 'border-border/60'
      }`}>
      <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-sm truncate max-w-sm">{email.subject || '(no subject)'}</p>
              {isAudited && (
                <span
                  data-testid="email-audited-badge"
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-700 border border-blue-200"
                  title="This email has already been audited"
                >
                  <CheckCircle2 className="w-3 h-3" />AUDITED
                </span>
              )}
              {isSaved && !isAudited && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-700">
                  <CheckCircle2 className="w-3 h-3" />Saved
                </span>
              )}
              {email.has_attachments && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] bg-blue-50 text-blue-600">
                  <Paperclip className="w-3 h-3" />Attachments
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              <span className="font-medium text-foreground/80">{email.from?.replace(/<.*>/, '').trim()}</span>
              <span className="ml-2 opacity-50">{formatDateShort(email.date)}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-1 opacity-70">{email.snippet}</p>
            {mode === 'saved' && email.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {email.attachments.map((a, i) => <AttChip key={i} filename={a.filename} size={a.size} />)}
              </div>
            )}
          </div>

          <div className="flex gap-1.5 flex-shrink-0">
            {mode === 'list' && (
              <>
                <Button size="sm" variant="outline" onClick={onView}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
                  title="View full email"
                  data-testid={`view-inbox-${email.gmail_message_id}`}>
                  <Eye className="w-3.5 h-3.5" />
                </Button>
                {!isSaved && (
                  <Button size="sm" variant="outline" onClick={onSave} disabled={saving}
                    className="h-7 text-xs gap-1 whitespace-nowrap">
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
                    Save
                  </Button>
                )}
              </>
            )}
            {mode === 'saved' && (
              <>
                <Button size="sm" variant="outline" onClick={onView}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
                  title="View full email">
                  <Eye className="w-3.5 h-3.5" />
                </Button>
                {isAudited ? (
                  <Button size="sm" variant="outline" onClick={onAudit}
                    className="h-7 text-xs gap-1 border-blue-300 text-blue-700 hover:bg-blue-50">
                    <Eye className="w-3 h-3" />View Audit
                  </Button>
                ) : (
                  <Button size="sm" onClick={onAudit} disabled={auditing} className="h-7 text-xs gap-1">
                    {auditing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                    {auditing ? 'Starting…' : 'Audit'}
                  </Button>
                )}
                <Button size="sm" variant="ghost"
                  className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                  onClick={onDelete}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────
const DATE_FILTERS = [
  { key: 'today',     label: 'Today',      q: () => `after:${fmt(0)}` },
  { key: 'yesterday', label: 'Yesterday',  q: () => `after:${fmt(1)} before:${fmt(0)}` },
  { key: 'week',      label: 'This Week',  q: () => `after:${fmt(7)}` },
  { key: 'month',     label: 'This Month', q: () => `after:${fmt(30)}` },
  { key: '3months',   label: 'Last 3 Mo.', q: () => `after:${fmt(90)}` },
];
function fmt(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}`;
}

// Combine builder filters into one Gmail query string.
function buildCombinedQuery({ dateKey, fromVal, subjectVal, hasAttachment }) {
  const parts = [];
  if (dateKey) {
    const f = DATE_FILTERS.find(f => f.key === dateKey);
    if (f) parts.push(f.q());
  }
  const from = (fromVal || '').trim();
  if (from) parts.push(`from:${from}`);
  const sub = (subjectVal || '').trim();
  if (sub)  parts.push(`subject:${sub.includes(' ') ? `"${sub}"` : sub}`);
  if (hasAttachment) parts.push('has:attachment');
  return parts.join(' ');
}

// ─ Pagination helpers ────────────────────────────
function paginate(items, page, pageSize) {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}
function totalPages(items, pageSize) {
  return Math.max(1, Math.ceil(items.length / pageSize));
}

function PaginationBar({ page, total, pageSize, count, onChange, testid }) {
  if (count <= pageSize) return null;
  const pages = totalPages([...Array(count)], pageSize);
  const start = (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, count);
  return (
    <div className="flex items-center justify-between gap-3 mt-3 px-2 text-xs" data-testid={testid}>
      <span className="text-muted-foreground">
        Showing <strong className="text-foreground">{start}–{end}</strong> of <strong className="text-foreground">{count}</strong>
      </span>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => onChange(1)} disabled={page === 1}
          className="h-7 px-2 rounded-md border border-border hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed">«</button>
        <button type="button" onClick={() => onChange(page - 1)} disabled={page === 1}
          data-testid={`${testid}-prev`}
          className="h-7 px-2 rounded-md border border-border hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed">‹</button>
        <span className="px-2 text-muted-foreground">
          Page <strong className="text-foreground">{page}</strong> / {pages}
        </span>
        <button type="button" onClick={() => onChange(page + 1)} disabled={page >= pages}
          data-testid={`${testid}-next`}
          className="h-7 px-2 rounded-md border border-border hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed">›</button>
        <button type="button" onClick={() => onChange(pages)} disabled={page >= pages}
          className="h-7 px-2 rounded-md border border-border hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed">»</button>
      </div>
    </div>
  );
}

// ─ Smart Search Popover ─
function SmartSearchPopover({ open, recentEmails, loadingRecent, savedIds,
  fromFilter, subjectFilter, dateKey, hasAttachment,
  setFrom, setSubject, setDateKey, setHasAttachment,
  onApply, onSelectEmail, onClose }) {

  const groups = groupEmails(
    recentEmails.filter(e => {
      const ff = fromFilter.trim().toLowerCase();
      const sf = subjectFilter.trim().toLowerCase();
      if (ff && !e.from?.toLowerCase().includes(ff)) return false;
      if (sf && !e.subject?.toLowerCase().includes(sf)) return false;
      return true;
    })
  );

  const hasAnyFilter = !!(dateKey || fromFilter.trim() || subjectFilter.trim() || hasAttachment);

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 4, scale: 0.97 }}
      transition={{ duration: 0.15 }}
      className="absolute top-full left-0 right-0 z-50 mt-2 rounded-2xl border border-border bg-white shadow-2xl overflow-hidden"
      style={{ maxHeight: '78vh' }}
      data-testid="gmail-search-popover"
    >
      {/* Filter builder */}
      <div className="px-4 pt-3 pb-3 border-b border-border/60 bg-secondary/30">
        {/* Date row — click to TOGGLE; selected stays highlighted */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1 w-[60px]">
            <Calendar className="w-3 h-3" />Date
          </span>
          {DATE_FILTERS.map(({ key, label }) => {
            const active = dateKey === key;
            return (
              <button
                type="button" key={key}
                onClick={() => setDateKey(active ? '' : key)}
                data-testid={`gmail-date-chip-${key}`}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : 'bg-white border-border hover:border-primary hover:bg-accent hover:text-primary'
                }`}
              >
                {label}
              </button>
            );
          })}
          {dateKey && (
            <button type="button" onClick={() => setDateKey('')}
              className="text-[10px] text-muted-foreground hover:text-red-600 underline ml-1">
              clear
            </button>
          )}
        </div>

        {/* From + Subject row */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <input
              type="text" placeholder="From sender…" value={fromFilter}
              onChange={e => setFrom(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onApply(); }}}
              data-testid="gmail-filter-from"
              className="w-full h-8 pl-7 pr-2 text-xs border border-border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
          </div>
          <div className="relative flex-1">
            <FileText className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <input
              type="text" placeholder="Subject contains…" value={subjectFilter}
              onChange={e => setSubject(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onApply(); }}}
              data-testid="gmail-filter-subject"
              className="w-full h-8 pl-7 pr-2 text-xs border border-border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
          </div>
        </div>

        {/* Has-attachment + Apply row */}
        <div className="flex items-center justify-between gap-2 mt-2">
          <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
            <input
              type="checkbox" checked={hasAttachment}
              onChange={e => setHasAttachment(e.target.checked)}
              data-testid="gmail-filter-has-attachment"
              className="rounded border-border accent-primary"
            />
            <Paperclip className="w-3 h-3 text-muted-foreground" />
            Has attachment
          </label>
          <div className="flex gap-2">
            {hasAnyFilter && (
              <button type="button" data-testid="gmail-clear-filters"
                onClick={() => { setDateKey(''); setFrom(''); setSubject(''); setHasAttachment(false); }}
                className="px-2.5 py-1 rounded-lg text-xs font-medium border border-border hover:bg-secondary text-muted-foreground">
                Clear
              </button>
            )}
            <button type="button"
              onClick={onApply}
              disabled={!hasAnyFilter}
              data-testid="gmail-apply-filters"
              className="px-3 py-1 rounded-lg text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1">
              <Search className="w-3 h-3" />Apply
            </button>
          </div>
        </div>
      </div>

      {/* Email list (live-filtered preview) */}
      <div className="overflow-y-auto" style={{ maxHeight: 'calc(78vh - 200px)' }}>
        {loadingRecent ? (
          <div className="p-4 space-y-2">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
          </div>
        ) : groups.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No emails match your filters</div>
        ) : (
          groups.map(({ label, items }) => (
            <div key={label}>
              <div className="sticky top-0 px-4 py-1.5 bg-secondary/60 backdrop-blur-sm border-b border-border/40">
                <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />{label}
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-border text-[10px]">{items.length}</span>
                </span>
              </div>
              {items.map(email => (
                <button type="button" key={email.gmail_message_id}
                  onClick={() => { onSelectEmail(email); onClose(); }}
                  className="w-full text-left px-4 py-2.5 hover:bg-accent/60 transition-colors border-b border-border/20 last:border-0">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-primary">{(email.from || '?').charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-semibold truncate flex-1">{email.subject || '(no subject)'}</p>
                        <span className="text-[10px] text-muted-foreground flex-shrink-0">{formatDateShort(email.date)}</span>
                        {savedIds.has(email.gmail_message_id) && <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />}
                        {email.has_attachments && <Paperclip className="w-3 h-3 text-blue-400 flex-shrink-0" />}
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate mt-0.5">{email.from?.replace(/<.*>/, '').trim()}</p>
                      <p className="text-[11px] text-muted-foreground/60 truncate">{email.snippet?.slice(0, 80)}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ))
        )}
      </div>

      <div className="px-4 py-2 border-t border-border/40 bg-secondary/20 flex items-center gap-2">
        <Search className="w-3 h-3 text-muted-foreground" />
        <span className="text-[11px] text-muted-foreground">
          Combine filters above & hit Apply · or type any Gmail operator in the search bar
        </span>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────
export default function Emails() {
  const navigate    = useNavigate();
  const [searchParams] = useSearchParams();

  const [status, setStatus]           = useState(null);
  const [loadingStatus, setLS]        = useState(true);
  const [searchQuery, setSearch]      = useState('');
  const [searching, setSearching]     = useState(false);
  const [results, setResults]         = useState([]);
  const [savedEmails, setSaved]       = useState([]);
  const [loadingSaved, setLoadSaved]  = useState(false);
  const [savingId, setSavingId]       = useState(null);
  const [auditingId, setAuditingId]   = useState(null);
  const [activeTab, setActiveTab]     = useState('inbox');
  const [viewEmail, setViewEmail]     = useState(null); // email to show in modal

  // Popover state
  const [popoverOpen, setPopover]     = useState(false);
  const [recentEmails, setRecent]     = useState([]);
  const [loadingRecent, setLR]        = useState(false);
  const [fromFilter, setFrom]         = useState('');
  const [subjectFilter, setSubject]   = useState('');
  const [dateKey, setDateKey]         = useState('');
  const [hasAttachment, setHasAttachment] = useState(false);
  const searchRef                     = useRef(null);
  const popoverRef                    = useRef(null);

  // Pagination state (per tab)
  const PAGE_SIZE = 25;
  const [inboxPage, setInboxPage] = useState(1);
  const [savedPage, setSavedPage] = useState(1);
  const [disconnecting, setDisconnecting] = useState(false);

  const savedIds = new Set(savedEmails.map(e => e.gmail_message_id));

  useEffect(() => {
    if (searchParams.get('connected') === '1') toast.success('Gmail connected!');
    if (searchParams.get('error'))             toast.error(`OAuth error: ${searchParams.get('error')}`);
  }, [searchParams]);

  const fetchStatus = useCallback(async () => {
    setLS(true);
    try { 
      const token = localStorage.getItem('access_token');
      const r = await fetch(`${BACKEND}/api/gmail/status`, {
        headers: { Authorization: `Bearer ${token}` }
      }); 
      const data = await r.json();
      setStatus(data);
    } catch(e) {
      setStatus({connected: false});
    } finally { setLS(false); }
  }, []);

  const fetchSaved = useCallback(async () => {
    setLoadSaved(true);
    try { const r = await fetch(`${BACKEND}/api/gmail/saved`); const d = await r.json(); setSaved(d.emails || []); }
    finally { setLoadSaved(false); }
  }, []);

  const fetchRecent = useCallback(async () => {
    setLR(true);
    try { const r = await fetch(`${BACKEND}/api/gmail/recent?limit=100`); const d = await r.json(); setRecent(d.messages || []); }
    finally { setLR(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { if (status?.connected) { fetchSaved(); fetchRecent(); } }, [status, fetchSaved, fetchRecent]);

  // Close popover on outside click
  useEffect(() => {
    function handler(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target) &&
          searchRef.current && !searchRef.current.contains(e.target)) {
        setPopover(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleConnect    = () => { window.location.href = `${BACKEND}/api/gmail/connect`; };
  const handleDisconnect = async () => {
    if (disconnecting) return;             // prevent double-click
    setDisconnecting(true);
    // Optimistically clear local state so the button unmounts immediately and a
    // rapid second click can't hit the now-401 endpoint via the global fetch
    // patch (which would redirect to /login mid-flight and surface a "body
    // stream already read" error in the console).
    setStatus({ connected: false }); setSaved([]); setResults([]); setRecent([]);
    try {
      const res = await fetch(`${BACKEND}/api/gmail/disconnect`, { method: 'DELETE' });
      // Drain the body explicitly to avoid any "body stream" warnings from
      // downstream observers (the response is small JSON: {"disconnected": true}).
      try { await res.text(); } catch { /* ignore */ }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Gmail disconnected');
    } catch (err) {
      // Tokens are already gone server-side in the happy path; re-show the
      // connect CTA either way.
      toast.error(`Disconnect failed: ${err.message}`);
    } finally {
      setDisconnecting(false);
    }
  };

  const runSearch = useCallback(async (q) => {
    if (!q?.trim()) return;
    setSearching(true); setPopover(false); setActiveTab('inbox');
    setInboxPage(1);
    try {
      const res  = await fetch(`${BACKEND}/api/gmail/search?q=${encodeURIComponent(q)}&limit=200`);
      const data = await res.json();
      setResults(data.messages || []);
      if (!data.messages?.length) toast.info('No emails found.');
      else toast.success(`Found ${data.messages.length} email${data.messages.length === 1 ? '' : 's'}`);
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  }, []);

  const handleSearch = (e) => { e?.preventDefault(); runSearch(searchQuery); };

  const handleApplyFilters = () => {
    const q = buildCombinedQuery({ dateKey, fromVal: fromFilter, subjectVal: subjectFilter, hasAttachment });
    if (!q) return;
    setSearch(q);   // Reflect in the search bar so the user sees the actual query
    runSearch(q);
  };

  const handleSave = async (gmailMsgId) => {
    setSavingId(gmailMsgId);
    try {
      const r = await fetch(`${BACKEND}/api/gmail/saved`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gmail_message_id: gmailMsgId }),
      });
      const d = await r.json();
      if (d.already_saved) toast.info('Already saved!'); else toast.success('Saved for auditing!');
      setResults(prev => prev.map(m => m.gmail_message_id === gmailMsgId ? { ...m, is_saved: true } : m));
      await fetchSaved();
    } finally { setSavingId(null); }
  };

  const handleSelectFromPopover = async (email) => {
    setSavingId(email.gmail_message_id);
    try {
      const r = await fetch(`${BACKEND}/api/gmail/saved`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gmail_message_id: email.gmail_message_id }),
      });
      const d = await r.json();
      if (d.already_saved) toast.info('Already saved — check Saved tab');
      else toast.success('Saved! See Saved for Audit tab.');
      await fetchSaved();
      setActiveTab('saved');
    } finally { setSavingId(null); }
  };

  const handleDelete = async (docId) => {
    await fetch(`${BACKEND}/api/gmail/saved/${docId}`, { method: 'DELETE' , headers: getAuthHeaders()});
    setSaved(prev => prev.filter(e => e._id !== docId));
    toast.success('Removed');
  };

  const handleAudit = async (email) => {
    // If already audited → jump to existing audit
    if (email.is_audited && email.audit_id) {
      navigate(`/results/${email.audit_id}`);
      return;
    }
    if (!email._id) {
      toast.error('Email is not saved yet — save it first.');
      return;
    }
    setAuditingId(email._id);
    toast.loading('Starting audit…', { id: 'audit-start' });
    try {
      const res = await fetch(`${BACKEND}/api/audit/from-gmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ saved_email_id: email._id }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const { job_id } = await res.json();
      toast.success('Audit started — running…', { id: 'audit-start' });
      // Hand off to NewAudit which already has full job-progress UI;
      // it will pick up `autoStartJobId` and tail the live log.
      navigate('/new-audit', { state: { gmailEmail: email, autoStartJobId: job_id } });
    } catch (e) {
      toast.error(`Audit failed: ${e.message}`, { id: 'audit-start' });
    } finally {
      setAuditingId(null);
    }
  };

  if (loadingStatus) return (
    <div className="page-pad max-w-[1100px] space-y-4">
      <Skeleton className="h-10 w-48" /><Skeleton className="h-40 w-full" />
    </div>
  );

  return (
    <div className="page-pad max-w-[1100px]">
      {/* Email view modal */}
      <AnimatePresence>
        {viewEmail && (
          <EmailViewModal
            key={viewEmail._id || viewEmail.gmail_message_id}
            email={viewEmail}
            onClose={() => setViewEmail(null)}
            onAudit={handleAudit}
            onSave={async (gmailMsgId) => {
              await handleSave(gmailMsgId);
              setViewEmail(null);
            }}
            saving={savingId === viewEmail.gmail_message_id}
          />
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-3"
          style={{ fontFamily: 'Space Grotesk' }}>
          <GmailIcon size={28} />Gmail Inbox
        </h1>
        <p className="text-muted-foreground text-sm mt-0.5">Search reorder emails and save them for auditing.</p>
      </div>

      {/* Not connected */}
      {!status?.connected && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-br from-[#4285F4]/8 via-[#EA4335]/5 to-[#FBBC05]/5 p-8 sm:p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-5 rounded-2xl shadow-lg bg-white flex items-center justify-center">
                <GmailIcon size={44} />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Connect your Gmail</h2>
              <p className="text-muted-foreground text-sm max-w-md mx-auto mb-8">
                Securely connect your Gmail to search reorder emails, fetch attachments, and audit directly.
              </p>
              <Button onClick={handleConnect} size="lg" className="gap-3 px-8 h-12 text-base shadow-md" style={{ background: '#4285F4' }}>
                <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#fff"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#fff"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#fff"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#fff"/></svg>
                Sign in with Google
              </Button>
              <p className="text-xs text-muted-foreground mt-4">Read-only access · Gmail messages only · No email sending</p>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Connected */}
      {status?.connected && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
          {/* Status bar */}
          <Card className="border-emerald-200 bg-emerald-50/40">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">Connected: {status.email}</p>
                    <p className="text-xs text-emerald-600">{status.messages_total?.toLocaleString()} messages in inbox</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchRecent} className="gap-1.5 h-8 text-xs">
                    <RefreshCw className="w-3.5 h-3.5" />Refresh
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDisconnect}
                    disabled={disconnecting}
                    data-testid="gmail-disconnect-btn"
                    className="gap-1.5 h-8 text-xs text-red-500 hover:text-red-600 hover:border-red-300 disabled:opacity-60 disabled:cursor-not-allowed">
                    {disconnecting
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Unlink className="w-3.5 h-3.5" />}
                    {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Smart search */}
          <div className="relative" ref={popoverRef}>
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input ref={searchRef} value={searchQuery} onChange={e => setSearch(e.target.value)}
                  onFocus={() => setPopover(true)}
                  placeholder='Search any email — try: from:levi@ subject:"reorder" after:2026/05/01 has:attachment'
                  className="w-full h-11 pl-9 pr-3 text-sm border-2 border-border rounded-xl bg-white focus:outline-none focus:border-primary transition-colors"
                  disabled={searching} autoComplete="off"
                  data-testid="gmail-search-input" />
                {searchQuery && (
                  <button type="button" onClick={() => { setSearch(''); setResults([]); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button type="submit" className="h-11 px-5 gap-2 rounded-xl" disabled={searching || !searchQuery.trim()} data-testid="gmail-search-button">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Search
              </Button>
            </form>

            {/* Result-count + Gmail-operator hint */}
            <div className="flex items-center justify-between mt-1.5 px-1 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Search className="w-3 h-3" />
                Searches your <strong className="font-semibold">whole mailbox</strong> using Gmail operators
                <span className="hidden md:inline opacity-70">
                  · <code className="font-mono bg-secondary px-1 rounded">from:</code>
                  <code className="font-mono bg-secondary px-1 rounded ml-1">subject:</code>
                  <code className="font-mono bg-secondary px-1 rounded ml-1">after:</code>
                  <code className="font-mono bg-secondary px-1 rounded ml-1">before:</code>
                  <code className="font-mono bg-secondary px-1 rounded ml-1">has:attachment</code>
                  <code className="font-mono bg-secondary px-1 rounded ml-1">label:</code>
                </span>
              </span>
              {results.length > 0 && (
                <span className="font-semibold text-foreground" data-testid="gmail-search-result-count">
                  {results.length} result{results.length === 1 ? '' : 's'}
                </span>
              )}
            </div>

            <AnimatePresence>
              {popoverOpen && (
                <SmartSearchPopover
                  open={popoverOpen}
                  recentEmails={recentEmails}
                  loadingRecent={loadingRecent}
                  savedIds={savedIds}
                  fromFilter={fromFilter}
                  subjectFilter={subjectFilter}
                  dateKey={dateKey}
                  hasAttachment={hasAttachment}
                  setFrom={setFrom}
                  setSubject={setSubject}
                  setDateKey={setDateKey}
                  setHasAttachment={setHasAttachment}
                  onApply={handleApplyFilters}
                  onSelectEmail={handleSelectFromPopover}
                  onClose={() => setPopover(false)}
                />
              )}
            </AnimatePresence>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="inbox" className="gap-2">
                <Inbox className="w-4 h-4" />Search Results
                {results.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-primary/10 text-primary rounded-full">{results.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="saved" className="gap-2">
                <Star className="w-4 h-4" />Saved for Audit
                {savedEmails.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-emerald-100 text-emerald-700 rounded-full">{savedEmails.length}</span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="inbox" className="space-y-3">
              {searching && <div className="space-y-2">{[...Array(4)].map((_,i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>}
              {!searching && results.length === 0 && (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center">
                    <Mail className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {searchQuery ? 'No results. Try a different query.' : 'Search or click a date filter in the dropdown.'}
                    </p>
                  </CardContent>
                </Card>
              )}
              <AnimatePresence mode="popLayout">
                {paginate(results, inboxPage, PAGE_SIZE).map(email => (
                  <EmailRow key={email.gmail_message_id} email={email}
                    isSaved={savedIds.has(email.gmail_message_id)}
                    onSave={() => handleSave(email.gmail_message_id)}
                    onView={() => setViewEmail(email)}
                    saving={savingId === email.gmail_message_id}
                    mode="list" />
                ))}
              </AnimatePresence>
              <PaginationBar
                page={inboxPage}
                total={totalPages(results, PAGE_SIZE)}
                pageSize={PAGE_SIZE}
                count={results.length}
                onChange={(p) => { setInboxPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                testid="gmail-inbox-pagination"
              />
            </TabsContent>

            <TabsContent value="saved" className="space-y-3">
              {loadingSaved && <div className="space-y-2">{[...Array(3)].map((_,i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>}
              {!loadingSaved && savedEmails.length === 0 && (
                <Card className="border-dashed">
                  <CardContent className="py-10 text-center">
                    <Star className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">No saved emails yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Click the search bar and select emails, or search and save from results.</p>
                  </CardContent>
                </Card>
              )}
              <AnimatePresence mode="popLayout">
                {paginate(savedEmails, savedPage, PAGE_SIZE).map(email => (
                  <EmailRow key={email._id} email={email}
                    isSaved={true}
                    onDelete={() => handleDelete(email._id)}
                    onAudit={() => handleAudit(email)}
                    onView={() => setViewEmail(email)}
                    auditing={auditingId === email._id}
                    mode="saved" />
                ))}
              </AnimatePresence>
              <PaginationBar
                page={savedPage}
                total={totalPages(savedEmails, PAGE_SIZE)}
                pageSize={PAGE_SIZE}
                count={savedEmails.length}
                onChange={(p) => { setSavedPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                testid="gmail-saved-pagination"
              />
            </TabsContent>
          </Tabs>
        </motion.div>
      )}
    </div>
  );
}
